import React, { useMemo } from 'react';
import { Modal, Row, Col, Typography, Card, Space, Divider, Tooltip as AntTooltip, Grid } from 'antd';
import { 
    PieChartOutlined, 
    ArrowUpOutlined, 
    ArrowDownOutlined,
    CalendarOutlined, 
    CrownOutlined, 
    ArrowRightOutlined,
    InfoCircleOutlined,
    LineChartOutlined
} from '@ant-design/icons';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title as ChartTitle,
    Filler
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, ChartTitle);

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Componentes auxiliares definidos fora para evitar erros de hoisting/undefined
const BadgeComponent = ({ color, text }) => (
    <Space size={6}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
        <Text style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{text}</Text>
    </Space>
);

const KpiCard = ({ title, value, icon, color, subtext, formatCurrency }) => {
    const isLongText = typeof value === 'string' && value.length > 18;
    const isLargeNumber = typeof value === 'string' && value.includes('R$') && value.length > 13;

    return (
        <Card className="premium-card" style={{ height: '100%', borderRadius: '16px' }} bodyStyle={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text type="secondary" style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>
                        {title}
                    </Text>
                    <div style={{ 
                        fontSize: isLargeNumber ? '17px' : isLongText ? '15px' : '20px', 
                        fontWeight: 800, 
                        color: '#1e293b',
                        lineHeight: 1.2,
                        whiteSpace: (isLargeNumber) ? 'nowrap' : 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        margin: '4px 0'
                    }}>
                        {typeof value === 'number' ? formatCurrency(value) : value}
                    </div>
                    {subtext && (
                        <div style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {subtext}
                        </div>
                    )}
                </div>
                <div style={{ 
                    width: 32, height: 32, borderRadius: '8px', background: `${color}15`, color: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0
                }}>
                    {icon}
                </div>
            </div>
        </Card>
    );
};

export default function DashboardCustosModal({ open, onClose, data }) {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const formatCurrency = (value) =>
        Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    // Processamento avançado de dados
    const stats = useMemo(() => {
        if (!data || data.length === 0) return null;

        // Classifica pelo TIPO do lançamento (direção do movimento bancário),
        // exatamente como a Movimentação faz nos cards Créditos/Débitos. Antes o
        // dashboard também contava pela categoria (tipo === 'C' OU categoria de
        // crédito), o que somava como receita alguns débitos categorizados como
        // crédito — divergindo dos totais da tela.
        const despesas = data.filter(l => (l.tipo?.id || l.tipo) === 'D');
        const receitas = data.filter(l => (l.tipo?.id || l.tipo) === 'C');

        const custosPorCategoria = {};
        const receitasPorCategoria = {};
        const fluxoPorDia = {};
        
        let totalCustos = 0;
        let totalReceitas = 0;

        despesas.forEach(l => {
            const catName = l.categoria?.nome || 'Sem Categoria';
            const value = Number(l.valor || 0);
            custosPorCategoria[catName] = (custosPorCategoria[catName] || 0) + value;
            totalCustos += value;

            const dKey = l.dataReferencia ? new Date(l.dataReferencia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : 'S/D';
            if (!fluxoPorDia[dKey]) fluxoPorDia[dKey] = { r: 0, d: 0 };
            fluxoPorDia[dKey].d += value;
        });

        receitas.forEach(l => {
            const catName = l.categoria?.nome || 'Sem Categoria';
            const value = Number(l.valor || 0);
            receitasPorCategoria[catName] = (receitasPorCategoria[catName] || 0) + value;
            totalReceitas += value;

            const dKey = l.dataReferencia ? new Date(l.dataReferencia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : 'S/D';
            if (!fluxoPorDia[dKey]) fluxoPorDia[dKey] = { r: 0, d: 0 };
            fluxoPorDia[dKey].r += value;
        });

        // Categorias Top
        const topCategoryDespesa = Object.entries(custosPorCategoria).sort((a, b) => b[1] - a[1])[0] || ['-', 0];
        const topCategoryReceita = Object.entries(receitasPorCategoria).sort((a, b) => b[1] - a[1])[0] || ['-', 0];
        
        // Donut Custos
        const sortedCatsD = Object.entries(custosPorCategoria).sort((a, b) => b[1] - a[1]);
        const labelsD = sortedCatsD.slice(0, 5).map(c => c[0]);
        const valuesD = sortedCatsD.slice(0, 5).map(c => c[1]);

        // Donut Receitas
        const sortedCatsR = Object.entries(receitasPorCategoria).sort((a, b) => b[1] - a[1]);
        const labelsR = sortedCatsR.slice(0, 5).map(c => c[0]);
        const valuesR = sortedCatsR.slice(0, 5).map(c => c[1]);

        const sortedDays = Object.keys(fluxoPorDia).sort((a, b) => {
            const [d1, m1] = a.split('/');
            const [d2, m2] = b.split('/');
            return m1 !== m2 ? Number(m1) - Number(m2) : Number(d1) - Number(d2);
        });

        return {
            totalCustos,
            totalReceitas,
            topCategoryD: { name: topCategoryDespesa[0], value: topCategoryDespesa[1] },
            topCategoryR: { name: topCategoryReceita[0], value: topCategoryReceita[1] },
            donutD: {
                labels: labelsD,
                datasets: [{
                    data: valuesD,
                    backgroundColor: ['#e11d48', '#fb7185', '#f43f5e', '#fda4af', '#fecdd3'],
                    borderWidth: 0,
                    borderRadius: 4
                }]
            },
            donutR: {
                labels: labelsR,
                datasets: [{
                    data: valuesR,
                    backgroundColor: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'],
                    borderWidth: 0,
                    borderRadius: 4
                }]
            },
            evolution: {
                labels: sortedDays,
                datasets: [
                    {
                        label: 'Receitas',
                        data: sortedDays.map(d => fluxoPorDia[d].r),
                        borderColor: '#10b981',
                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        pointRadius: 0
                    },
                    {
                        label: 'Despesas',
                        data: sortedDays.map(d => fluxoPorDia[d].d),
                        borderColor: '#ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        pointRadius: 0
                    }
                ]
            }
        };
    }, [data]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1250}
            className="premium-modal-v2"
            destroyOnClose
            style={{ top: isMobile ? 0 : 20 }}
            bodyStyle={{ padding: isMobile ? '16px' : '24px', backgroundColor: '#f8fafc', borderRadius: '16px' }}
        >
            <header style={{ 
                marginBottom: 32, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 16
            }}>
                <Space size={16}>
                    <div style={{ 
                        width: 48, height: 48, borderRadius: '14px', 
                        background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        color: 'white', fontSize: '24px',
                        boxShadow: '0 8px 16px -4px rgba(15, 23, 42, 0.25)'
                    }}>
                        <LineChartOutlined />
                    </div>
                    <div>
                        <Title level={isMobile ? 5 : 4} style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>
                            Inteligência Financeira
                        </Title>
                        <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b' }}>
                            Análise estratégica de entradas, saídas e performance.
                        </Text>
                    </div>
                </Space>
                
                <div style={{ 
                    background: '#ffffff', 
                    padding: '8px 16px', 
                    borderRadius: '12px', 
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMobile ? 'flex-start' : 'flex-end',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}>
                    <Text type="secondary" style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em' }}>
                        Base de Dados
                    </Text>
                    <Text strong style={{ color: '#0f172a', fontSize: '14px' }}>
                        {data?.length || 0} <span style={{ fontWeight: 400, color: '#64748b' }}>Movimentações</span>
                    </Text>
                </div>
            </header>

            {!stats ? (
                <Card style={{ 
                    textAlign: 'center', 
                    padding: '100px 0', 
                    borderRadius: '24px', 
                    border: '2px dashed #e2e8f0',
                    background: '#f8fafc'
                }}>
                    <Space direction="vertical" size={16}>
                        <div style={{ fontSize: '48px', opacity: 0.2 }}>
                            <LineChartOutlined />
                        </div>
                        <div>
                            <Title level={4} style={{ color: '#64748b', margin: 0 }}>Aguardando dados...</Title>
                            <Text type="secondary">Nenhuma movimentação encontrada para o período selecionado.</Text>
                        </div>
                    </Space>
                </Card>
            ) : (
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    {/* KPI ROW */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} lg={6}>
                            <KpiCard title="Total Recebido" value={stats.totalReceitas} icon={<ArrowUpOutlined />} color="#10b981" subtext="Entradas no período" formatCurrency={formatCurrency} />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <KpiCard title="Total Pago" value={stats.totalCustos} icon={<ArrowDownOutlined />} color="#e11d48" subtext="Saídas no período" formatCurrency={formatCurrency} />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <KpiCard title="Maior Receita" value={stats.topCategoryR.name} icon={<CrownOutlined />} color="#10b981" subtext={`Total: ${formatCurrency(stats.topCategoryR.value)}`} formatCurrency={formatCurrency} />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <KpiCard title="Maior Despesa" value={stats.topCategoryD.name} icon={<CrownOutlined />} color="#e11d48" subtext={`Total: ${formatCurrency(stats.topCategoryD.value)}`} formatCurrency={formatCurrency} />
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        {/* RECEITAS */}
                        <Col xs={24} lg={12}>
                            <Card className="premium-card" style={{ borderRadius: '16px' }} title={<Text strong>Maiores Recebimentos por Categoria</Text>}>
                                <div style={{ height: 280 }}>
                                    <Bar 
                                        data={{
                                            labels: stats.donutR.labels,
                                            datasets: [{
                                                data: stats.donutR.datasets[0].data,
                                                backgroundColor: '#10b981',
                                                borderRadius: 6,
                                                barThickness: 24
                                            }]
                                        }}
                                        options={{
                                            indexAxis: 'y',
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: { x: { display: false }, y: { grid: { display: false }, ticks: { font: { weight: 600 } } } }
                                        }}
                                    />
                                </div>
                            </Card>
                        </Col>

                        {/* DESPESAS */}
                        <Col xs={24} lg={12}>
                            <Card className="premium-card" style={{ borderRadius: '16px' }} title={<Text strong>Maiores Despesas por Categoria</Text>}>
                                <div style={{ height: 280 }}>
                                    <Bar 
                                        data={{
                                            labels: stats.donutD.labels,
                                            datasets: [{
                                                data: stats.donutD.datasets[0].data,
                                                backgroundColor: '#e11d48',
                                                borderRadius: 6,
                                                barThickness: 24
                                            }]
                                        }}
                                        options={{
                                            indexAxis: 'y',
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: { x: { display: false }, y: { grid: { display: false }, ticks: { font: { weight: 600 } } } }
                                        }}
                                    />
                                </div>
                            </Card>
                        </Col>

                        {/* EVOLUTION LINE */}
                        <Col xs={24}>
                            <Card className="premium-card" style={{ borderRadius: '16px' }}>
                                <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <Text strong style={{ fontSize: '15px' }}>Evolução do Fluxo de Caixa</Text>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>Entradas vs Saídas diárias.</div>
                                    </div>
                                    <Space>
                                        <BadgeComponent color="#10b981" text="Entradas" />
                                        <BadgeComponent color="#ef4444" text="Saídas" />
                                    </Space>
                                </div>
                                <div style={{ height: 300 }}>
                                    <Line 
                                        data={stats.evolution}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                                                y: { grid: { color: '#f1f5f9' }, ticks: { font: { size: 10 }, callback: (v) => v >= 1000 ? (v/1000).toFixed(0) + 'k' : v } }
                                            },
                                            interaction: { mode: 'index', intersect: false }
                                        }}
                                    />
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Space>
            )}
        </Modal>
    );
}
